import { StandardEvents } from '@shopify/events';

/**
 * Storefront behaviour tracking for PostHog.
 * Loaded by snippets/posthog.liquid after the PostHog loader, so `window.posthog` is
 * always at least the queueing stub.
 *
 * Selectors below read existing theme markup (external product gallery, product sales
 * story, acacia advertorial). Renaming those classes silently stops the matching events.
 */

const MAIN_SECTION_SELECTOR = '.shopify-section:not([id*="header"]):not([id*="footer"]):not([id*="announcement"]):not([id*="cart-drawer"])';
const SUBSECTION_SELECTOR = [
  '.acacia-advertorial__header',
  '.acacia-advertorial__story',
  '.acacia-advertorial__offer',
  '.acacia-advertorial__footer',
  'section[class*="product-sales-story__"]',
  '.product-sales-story__image-strip',
].join(',');
const CTA_SELECTOR = '.acacia-advertorial__button, .acacia-advertorial__text-link';
const ADD_TO_CART_SELECTOR = 'button[name="add"], .add-to-cart-button, add-to-cart-component button';
const PRICE_SELECTOR = 'variant-picker, .variant-option__card-prices, .acacia-advertorial__offer';
const LANDING_KEY = 'hh_ph_landing';
const FIRST_IMAGE_KEY = 'hh_ph_first_image';

const pageLoadedAt = performance.now();

/**
 * @param {string} event
 * @param {Record<string, unknown>} [properties]
 * @param {boolean} [beacon] Use sendBeacon for events sent while the page is unloading.
 */
function capture(event, properties = {}, beacon = false) {
  window.posthog?.capture(event, properties, beacon ? { transport: 'sendBeacon' } : undefined);
}

/** @param {string | null | undefined} text */
function clean(text, max = 100) {
  return (text ?? '').replace(/\s+/g, ' ').trim().slice(0, max) || null;
}

/** @param {string | null | undefined} src */
function fileName(src) {
  if (!src) return null;
  try {
    return new URL(src, location.href).pathname.split('/').pop() ?? null;
  } catch {
    return null;
  }
}

function secondsSinceLoad() {
  return Math.round((performance.now() - pageLoadedAt) / 1000);
}

/** @param {Element} element */
function areaOf(element) {
  if (element.closest('sticky-add-to-cart, .acacia-advertorial__sticky')) return 'sticky';
  const id = element.closest('.shopify-section')?.id ?? '';
  if (id.includes('header') || id.includes('announcement')) return 'header';
  if (id.includes('footer')) return 'footer';
  if (id.includes('cart-drawer')) return 'cart_drawer';
  return 'main';
}

function productHandle() {
  return location.pathname.match(/\/products\/([^/?#]+)/)?.[1] ?? null;
}

/* ---------- Landing page attribution ---------- */

function registerLanding() {
  /** @type {Record<string, string | null> | null} */
  let landing = null;
  try {
    landing = JSON.parse(sessionStorage.getItem(LANDING_KEY) ?? 'null');
  } catch {}

  if (!landing) {
    const params = new URLSearchParams(location.search);
    const ctaHref = document.querySelector(`${CTA_SELECTOR}, main a[href*="/products/"]`)?.getAttribute('href');
    landing = {
      landing_page: location.pathname,
      landing_product: productHandle() ?? ctaHref?.match(/\/products\/([^/?#]+)/)?.[1] ?? null,
      landing_referrer: document.referrer || null,
      landing_utm_source: params.get('utm_source'),
      landing_utm_campaign: params.get('utm_campaign'),
      landing_utm_content: params.get('utm_content'),
      landing_fbclid: params.get('fbclid'),
    };
    try {
      sessionStorage.setItem(LANDING_KEY, JSON.stringify(landing));
    } catch {}
  }

  window.posthog?.register_for_session(landing);
  window.posthog?.register({ product_handle: productHandle() });
}

/* ---------- Sections: first view and time spent ---------- */

/** @typedef {{ name: string, heading: string | null, index: number, visibleSince: number | null, total: number, viewed: boolean }} TrackedSection */

/** @type {Map<Element, TrackedSection>} */
const sections = new Map();

/** @param {Element} element */
function sectionName(element) {
  const blockClass = [...element.classList].find((name) => name.includes('__'));
  if (blockClass) return blockClass.split('__')[1];
  const sectionId = element.id.replace(/^shopify-section-/, '').split('__')[1] ?? element.id;
  return sectionId.replace(/_[A-Za-z0-9]{6}$/, '') || 'section';
}

function collectSections() {
  /** @type {Element[]} */
  const tracked = [];
  for (const section of document.querySelectorAll(MAIN_SECTION_SELECTOR)) {
    const subsections = [...section.querySelectorAll(SUBSECTION_SELECTOR)].filter(
      (element) => !element.parentElement?.closest(SUBSECTION_SELECTOR)
    );
    tracked.push(...(subsections.length ? subsections : [section]));
  }
  return tracked;
}

/** @param {IntersectionObserverEntry} entry */
function isMeaningfullyVisible(entry) {
  if (!entry.isIntersecting) return false;
  const visibleHeight = entry.intersectionRect.height;
  return visibleHeight >= Math.min(window.innerHeight * 0.5, entry.boundingClientRect.height * 0.5);
}

function observeSections() {
  const observer = new IntersectionObserver(
    (entries) => {
      const now = performance.now();
      for (const entry of entries) {
        const section = sections.get(entry.target);
        if (!section) continue;
        const visible = isMeaningfullyVisible(entry) && !document.hidden;

        if (visible && section.visibleSince === null) {
          section.visibleSince = now;
          if (!section.viewed) {
            section.viewed = true;
            capture('section_viewed', {
              section: section.name,
              section_heading: section.heading,
              section_index: section.index,
              seconds_since_page_load: secondsSinceLoad(),
            });
          }
        } else if (!visible && section.visibleSince !== null) {
          section.total += now - section.visibleSince;
          section.visibleSince = null;
        }
      }
    },
    { threshold: Array.from({ length: 11 }, (_, index) => index / 10) }
  );

  collectSections().forEach((element, index) => {
    sections.set(element, {
      name: sectionName(element),
      heading: clean(element.querySelector('h1, h2')?.textContent, 80),
      index,
      visibleSince: null,
      total: 0,
      viewed: false,
    });
    observer.observe(element);
  });
}

/* ---------- Videos: viewed for 2s at 50% visibility, plus watch time ---------- */

/** @type {Map<HTMLVideoElement, { index: number, visibleSince: number | null, total: number, timer: number | undefined, viewed: boolean }>} */
const videos = new Map();

/** @param {HTMLVideoElement} video */
function videoProperties(video) {
  return {
    video_label: clean(video.getAttribute('aria-label') ?? video.closest('figure')?.textContent, 80),
    video_file: fileName(video.currentSrc || video.querySelector('source')?.src),
    video_index: videos.get(video)?.index ?? null,
    section: sectionOf(video),
  };
}

function observeVideos() {
  const observer = new IntersectionObserver(
    (entries) => {
      const now = performance.now();
      for (const entry of entries) {
        const video = /** @type {HTMLVideoElement} */ (entry.target);
        const state = videos.get(video);
        if (!state) continue;
        const visible = entry.intersectionRatio >= 0.5 && !document.hidden;

        if (visible && state.visibleSince === null) {
          state.visibleSince = now;
          if (!state.viewed) {
            state.timer = window.setTimeout(() => {
              state.viewed = true;
              capture('video_viewed', videoProperties(video));
            }, 2000);
          }
        } else if (!visible && state.visibleSince !== null) {
          state.total += now - state.visibleSince;
          state.visibleSince = null;
          window.clearTimeout(state.timer);
        }
      }
    },
    { threshold: [0, 0.5, 1] }
  );

  document.querySelectorAll('video').forEach((video, index) => {
    videos.set(video, { index, visibleSince: null, total: 0, timer: undefined, viewed: false });
    observer.observe(video);
  });
}

/** @param {Element} element */
function sectionOf(element) {
  for (const [sectionElement, section] of sections) {
    if (sectionElement.contains(element)) return section.name;
  }
  return null;
}

/* ---------- Flush accumulated time when the page is hidden ---------- */

function flushTimers() {
  const now = performance.now();

  for (const section of sections.values()) {
    if (section.visibleSince !== null) {
      section.total += now - section.visibleSince;
      section.visibleSince = null;
    }
    const seconds = Math.round(section.total / 1000);
    if (seconds >= 1) {
      capture(
        'section_time',
        { section: section.name, section_heading: section.heading, section_index: section.index, seconds_visible: seconds },
        true
      );
    }
    section.total = 0;
  }

  for (const [video, state] of videos) {
    if (state.visibleSince !== null) {
      state.total += now - state.visibleSince;
      state.visibleSince = null;
      window.clearTimeout(state.timer);
    }
    const seconds = Math.round(state.total / 1000);
    if (state.viewed && seconds >= 1) {
      capture('video_watch_time', { ...videoProperties(video), seconds_watched: seconds }, true);
    }
    state.total = 0;
  }
}

function resumeTimers() {
  // Observers only fire on visibility changes, so restart counting for what is on screen now.
  const now = performance.now();
  const viewportHeight = window.innerHeight;
  for (const [element, section] of sections) {
    const rect = element.getBoundingClientRect();
    const visibleHeight = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);
    if (visibleHeight >= Math.min(viewportHeight * 0.5, rect.height * 0.5)) section.visibleSince = now;
  }
  for (const [video, state] of videos) {
    const rect = video.getBoundingClientRect();
    const visibleHeight = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);
    if (rect.height && visibleHeight / rect.height >= 0.5) state.visibleSince = now;
  }
}

/* ---------- Scroll milestones ---------- */

/** @type {Set<string>} */
const milestones = new Set();

/** @param {string} milestone */
function reachMilestone(milestone) {
  if (milestones.has(milestone)) return;
  milestones.add(milestone);
  capture('scroll_milestone', { milestone, seconds_since_page_load: secondsSinceLoad() });
}

function observeScrollDepth() {
  // From 990px base.css makes .page-wrapper the scroll container; below that the document scrolls.
  const pageWrapper = document.querySelector('.page-wrapper');
  let scheduled = false;

  /** @param {Element} scroller */
  const check = (scroller) => {
    scheduled = false;
    const isDocument = scroller === document.scrollingElement;
    const top = scroller.scrollTop;
    const viewport = isDocument ? window.innerHeight : scroller.clientHeight;
    const depth = (top + viewport) / scroller.scrollHeight;
    if (top >= viewport) reachMilestone('first_screen_passed');
    for (const percent of [25, 50, 75]) {
      if (depth >= percent / 100) reachMilestone(`${percent}%`);
    }
    // Mobile browser bars change the viewport height, so allow a small gap at the bottom.
    if (scroller.scrollHeight - (top + viewport) <= 120) reachMilestone('100%');
  };

  document.addEventListener(
    'scroll',
    (event) => {
      const scroller = event.target === document ? document.scrollingElement : event.target;
      if (!(scroller instanceof Element) || (scroller !== document.scrollingElement && scroller !== pageWrapper)) return;
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => check(scroller));
    },
    { capture: true, passive: true }
  );

  const targets = [
    ['price_seen', document.querySelector(PRICE_SELECTOR)],
    [
      'add_to_cart_seen',
      [...document.querySelectorAll('add-to-cart-component, .acacia-advertorial__button')].find(
        (element) => !element.closest('sticky-add-to-cart, .acacia-advertorial__sticky')
      ),
    ],
    ['footer_seen', [...document.querySelectorAll('.shopify-section[id*="footer"], .acacia-advertorial__footer')].pop()],
  ];
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const target = targets.find(([, element]) => element === entry.target);
        if (target) reachMilestone(/** @type {string} */ (target[0]));
        observer.unobserve(entry.target);
      }
    },
    { threshold: 0.5 }
  );
  for (const [, element] of targets) {
    if (element instanceof Element) observer.observe(element);
  }
}

/* ---------- Clicks: images, add to cart, links ---------- */

let imageClicks = 0;

/** @param {Element} target */
function trackImageClick(target) {
  const thumbnail = target.closest('.external-product-gallery__thumbnail');
  const image = thumbnail?.querySelector('img') ?? target.closest('img');
  if (!image || areaOf(image) !== 'main') return;

  /** @type {Record<string, unknown>} */
  let properties;
  if (thumbnail instanceof HTMLElement) {
    const thumbnails = [...document.querySelectorAll('.external-product-gallery__thumbnail')];
    properties = {
      image_area: 'gallery_thumbnail',
      image_index: thumbnails.indexOf(thumbnail) + 1,
      image_alt: clean(thumbnail.dataset.alt ?? image.alt),
      image_file: fileName(thumbnail.dataset.source ?? image.currentSrc),
      variant_title: thumbnail.dataset.variantTitle ?? null,
    };
  } else {
    const container = image.closest('.external-product-gallery__stage') ? 'gallery_main' : 'content';
    const sectionElement = [...sections.keys()].find((element) => element.contains(image));
    const siblings = [...(sectionElement ?? document).querySelectorAll('img')];
    properties = {
      image_area: container,
      image_index: siblings.indexOf(image) + 1,
      image_alt: clean(image.alt),
      image_file: fileName(image.currentSrc || image.src),
    };
  }

  imageClicks += 1;
  let firstOfSession = false;
  try {
    firstOfSession = !sessionStorage.getItem(FIRST_IMAGE_KEY);
    if (firstOfSession) sessionStorage.setItem(FIRST_IMAGE_KEY, String(properties.image_file));
  } catch {}

  capture('product_image_clicked', {
    ...properties,
    section: sectionOf(image),
    is_first_image_click: imageClicks === 1,
    is_first_image_click_of_session: firstOfSession,
    image_click_number: imageClicks,
  });
}

/** @type {string | null} */
let lastClickedVariantId = null;

function selectedSetSize() {
  const checked = document.querySelector('variant-picker input[type="radio"]:checked');
  return checked instanceof HTMLInputElement ? checked.value : null;
}

/** @param {Element} button */
function trackAddToCartClick(button) {
  const form = button.closest('form') ?? document.querySelector('product-form-component form');
  const variantInput = form?.querySelector('[name="id"]');
  const priceText = document.querySelector('variant-picker input[type="radio"]:checked')
    ?.closest('label')
    ?.querySelector('.variant-option__card-prices')?.textContent;

  lastClickedVariantId = variantInput instanceof HTMLInputElement ? variantInput.value : null;
  capture('add_to_cart_clicked', {
    source: areaOf(button) === 'sticky' ? 'sticky' : 'main',
    variant_id: lastClickedVariantId,
    set_size: selectedSetSize(),
    price_text: clean(priceText, 60),
    seconds_since_page_load: secondsSinceLoad(),
  });
}

/** @param {HTMLAnchorElement} link */
function trackLinkClick(link) {
  let url;
  try {
    url = new URL(link.href, location.href);
  } catch {
    return;
  }
  if (!/^https?:$/.test(url.protocol)) return;
  if (url.origin === location.origin && url.pathname === location.pathname) return;

  const path = url.pathname;
  const destinationType =
    url.origin !== location.origin
      ? 'external'
      : path === '/'
        ? 'home'
        : (path.match(/^\/(products|collections|pages|policies|blogs|cart|account|search)/)?.[1] ?? 'other');

  /** @type {Record<string, unknown>} */
  const properties = {
    destination: url.origin === location.origin ? path : url.href,
    destination_type: destinationType,
    link_text: clean(link.textContent || link.getAttribute('aria-label'), 80),
    area: areaOf(link),
    section: sectionOf(link),
  };

  if (link.matches(CTA_SELECTOR)) {
    properties.cta_position = [...document.querySelectorAll(CTA_SELECTOR)].indexOf(link) + 1;
    properties.cta_type = link.closest('.acacia-advertorial__sticky')
      ? 'sticky'
      : link.classList.contains('acacia-advertorial__button')
        ? 'button'
        : 'text_link';
  }

  capture('link_clicked', properties, true);
}

function observeClicks() {
  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;
      const target = event.target;

      const addToCart = target.closest(ADD_TO_CART_SELECTOR);
      if (addToCart) {
        trackAddToCartClick(addToCart);
        return;
      }

      trackImageClick(target);

      const link = target.closest('a[href]');
      if (link instanceof HTMLAnchorElement) trackLinkClick(link);
    },
    { capture: true }
  );
}

/* ---------- Cart results ---------- */

function observeCart() {
  document.addEventListener(StandardEvents.cartLinesUpdate, async (event) => {
    const update = /** @type {Event & { action?: string, context?: string, lines?: { merchandiseId?: string, quantity?: number }[], promise?: Promise<unknown> }} */ (
      event
    );
    if (update.action !== 'add') return;
    // Set offers add free gifts in the same batch, so report the clicked variant rather than the first line.
    const variantIds = (update.lines ?? []).map((line) => line.merchandiseId?.split('/').pop() ?? null);
    const properties = {
      variant_id:
        lastClickedVariantId && variantIds.includes(lastClickedVariantId) ? lastClickedVariantId : variantIds[0] ?? null,
      variant_ids: variantIds,
      line_count: variantIds.length,
      quantity: (update.lines ?? []).reduce((sum, line) => sum + (line.quantity ?? 0), 0),
      context: update.context ?? null,
      set_size: selectedSetSize(),
    };
    try {
      await update.promise;
      capture('added_to_cart', properties);
      syncCartAttributes();
    } catch (error) {
      capture('add_to_cart_failed', { ...properties, error: clean(String(error), 120) });
    }
  });

  document.addEventListener(StandardEvents.cartError, (event) => {
    const error = /** @type {Event & { error?: string }} */ (event).error;
    capture('add_to_cart_failed', { error: clean(error, 120) });
  });
}

/* ---------- Checkout identity handoff ---------- */

const CART_SYNC_KEY = 'hh_ph_cart_sync';

/**
 * Copies the PostHog IDs and landing tags into hidden cart attributes. The Shopify custom pixel
 * (tools/posthog-custom-pixel.js) reads them in checkout, so purchases join this visitor's funnel.
 */
async function syncCartAttributes() {
  const cartToken = document.cookie.match(/(?:^|;\s*)cart=([^;]*)/)?.[1];
  if (!cartToken) return;

  // The loader stub has no getters until the full library arrives.
  for (let attempt = 0; attempt < 40 && !window.posthog?.__loaded; attempt++) {
    await new Promise((resolve) => setTimeout(resolve, 250));
  }
  const posthog = window.posthog;
  if (!posthog?.__loaded) return;

  const distinctId = posthog.get_distinct_id();
  const sessionId = posthog.get_session_id();
  const signature = `${cartToken}|${distinctId}|${sessionId}`;
  try {
    if (sessionStorage.getItem(CART_SYNC_KEY) === signature) return;
  } catch {}

  let landing = {};
  try {
    landing = JSON.parse(sessionStorage.getItem(LANDING_KEY) ?? '{}') ?? {};
  } catch {}

  /** @type {Record<string, string>} */
  const attributes = { _ph_distinct_id: distinctId, _ph_session_id: sessionId };
  for (const [key, value] of Object.entries(landing)) {
    if (value) attributes[`_ph_${key}`] = String(value).slice(0, 200);
  }

  try {
    const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes }),
    });
    if (response.ok) sessionStorage.setItem(CART_SYNC_KEY, signature);
  } catch {}
}

/* ---------- Variant options, FAQ, carousels, pinch zoom ---------- */

function observeInputs() {
  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement || input instanceof HTMLSelectElement)) return;
    if (!input.closest('variant-picker')) return;
    if (input instanceof HTMLInputElement && input.type === 'radio' && !input.checked) return;
    capture('variant_option_selected', {
      option_name: input.dataset.optionName ?? input.closest('fieldset')?.querySelector('legend')?.textContent?.trim() ?? null,
      value: input.value,
      source: areaOf(input) === 'sticky' ? 'sticky' : 'main',
    });
  });

  // `toggle` does not bubble, so listen in the capture phase.
  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement) || !details.open || areaOf(details) !== 'main') return;
      const items = [...(details.parentElement?.querySelectorAll(':scope > details') ?? [])];
      capture('faq_opened', {
        question: clean(details.querySelector('summary')?.textContent),
        question_index: items.indexOf(details) + 1,
        section: sectionOf(details),
      });
    },
    { capture: true }
  );
}

function observeCarousels() {
  /** @type {WeakMap<Element, { left: number, timer: number | undefined }>} */
  const scrollState = new WeakMap();
  /** @type {{ element: Element | null, at: number }} */
  const lastTouch = { element: null, at: 0 };

  // Only finger drags and wheel/trackpad gestures count; a tap that scrolls a thumbnail into view does not.
  for (const type of ['touchmove', 'wheel']) {
    document.addEventListener(
      type,
      (event) => {
        if (event.target instanceof Element) lastTouch.element = event.target;
        lastTouch.at = performance.now();
      },
      { capture: true, passive: true }
    );
  }

  document.addEventListener(
    'scroll',
    (event) => {
      const element = event.target;
      if (!(element instanceof Element) || element.scrollWidth <= element.clientWidth + 4) return;
      // Ignore programmatic scrolling such as thumbnails scrolled into view.
      if (performance.now() - lastTouch.at > 1500 || !lastTouch.element || !element.contains(lastTouch.element)) return;

      const state = scrollState.get(element) ?? { left: element.scrollLeft, timer: undefined };
      scrollState.set(element, state);
      window.clearTimeout(state.timer);
      state.timer = window.setTimeout(() => {
        const delta = element.scrollLeft - state.left;
        state.left = element.scrollLeft;
        if (Math.abs(delta) < 10) return;
        const slideWidth = element.scrollWidth / Math.max(element.children.length, 1);
        capture('carousel_swiped', {
          carousel: sectionName(element),
          direction: delta > 0 ? 'forward' : 'back',
          slide_index: Math.round(Math.abs(element.scrollLeft) / slideWidth) + 1,
          slide_count: element.children.length,
          section: sectionOf(element),
        });
      }, 250);
    },
    { capture: true, passive: true }
  );

  document.addEventListener('slideshow:select', (event) => {
    const detail = /** @type {CustomEvent<{ index: number, previousIndex: number, userInitiated: boolean, trigger: string }>} */ (
      event
    ).detail;
    if (!detail?.userInitiated || !(event.target instanceof Element)) return;
    capture('carousel_swiped', {
      carousel: event.target.localName,
      direction: detail.index > detail.previousIndex ? 'forward' : 'back',
      slide_index: detail.index + 1,
      trigger: detail.trigger,
      area: areaOf(event.target),
    });
  });
}

function observePinchZoom() {
  /** @type {WeakSet<Element>} */
  const zoomed = new WeakSet();
  document.addEventListener(
    'touchstart',
    (event) => {
      if (event.touches.length < 2 || !(event.target instanceof Element)) return;
      const media =
        event.target.closest('.external-product-gallery__stage, picture, figure') ?? event.target.closest('img, video');
      if (!media || zoomed.has(media)) return;
      zoomed.add(media);
      const image = media.matches('img, video') ? media : media.querySelector('img, video');
      capture('image_pinch_zoomed', {
        image_alt: clean(image?.getAttribute('alt') ?? image?.getAttribute('aria-label')),
        image_file: fileName(image instanceof HTMLImageElement ? image.currentSrc || image.src : image?.querySelector('source')?.src),
        image_area: media.closest('.external-product-gallery') ? 'gallery_main' : 'content',
        section: sectionOf(media),
      });
    },
    { capture: true, passive: true }
  );
}

/* ---------- Init ---------- */

registerLanding();
observeSections();
observeVideos();
observeScrollDepth();
observeClicks();
observeCart();
observeInputs();
observeCarousels();
observePinchZoom();
// Covers carts created before this page, and new PostHog sessions on an existing cart.
syncCartAttributes();

document.addEventListener('visibilitychange', () => {
  if (document.hidden) flushTimers();
  else resumeTimers();
});
window.addEventListener('pagehide', flushTimers);
